/**
 * Shared types + sessionStorage helpers for the producer qualification flow.
 *
 * v2: Place-first model. Producer selects a curated place, then provides
 * their business name and offerings. No place creation in this flow.
 */

const STORAGE_KEY = 'farmly_producer_qualify_v2';

export interface QualifySelectedPlace {
  id: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
  description_short: string | null;
}

export interface QualifyData {
  /** Selected canonical place */
  selected_place: QualifySelectedPlace | null;
  /** Producer/business name (separate from place) */
  producer_name: string;
  /** Offering type keys from offering catalog */
  offerings: string[];
  /** Contact email for non-logged-in users */
  contact_email: string;
  /** ISO timestamp */
  created_at_iso: string;
}

export function saveQualifyData(data: QualifyData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    if (import.meta.env.DEV) {
      // Verify round-trip immediately
      const readback = sessionStorage.getItem(STORAGE_KEY);
      const parsed = readback ? JSON.parse(readback) : null;
      console.log('QUALIFY SAVE VERIFY', {
        savedPlaceId: data.selected_place?.id,
        readbackPlaceId: parsed?.selected_place?.id,
        roundTripOk: data.selected_place?.id === parsed?.selected_place?.id,
      });
    }
  } catch {
    // silent
  }
}

function normalizeSelectedPlace(value: unknown): QualifySelectedPlace | null {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Partial<QualifySelectedPlace>;
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : '';
  const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';

  if (!id || !name) return null;

  return {
    id,
    name,
    municipality: typeof candidate.municipality === 'string' ? candidate.municipality : null,
    landskap: typeof candidate.landskap === 'string' ? candidate.landskap : null,
    description_short:
      typeof candidate.description_short === 'string' ? candidate.description_short : null,
  };
}

export function readQualifyData(): QualifyData | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<QualifyData>;
    const normalizedPlace = normalizeSelectedPlace(parsed.selected_place);

    if (import.meta.env.DEV) {
      console.log('QUALIFY READ', {
        rawPlaceKeys: parsed.selected_place ? Object.keys(parsed.selected_place as object) : null,
        rawPlaceId: (parsed.selected_place as unknown as Record<string, unknown>)?.id,
        normalizedPlace,
        normalizedPlaceId: normalizedPlace?.id,
      });
    }

    return {
      selected_place: normalizedPlace,
      producer_name: typeof parsed.producer_name === 'string' ? parsed.producer_name : '',
      offerings: Array.isArray(parsed.offerings)
        ? parsed.offerings.filter((item): item is string => typeof item === 'string' && item.length > 0)
        : [],
      contact_email: typeof parsed.contact_email === 'string' ? parsed.contact_email : '',
      created_at_iso:
        typeof parsed.created_at_iso === 'string'
          ? parsed.created_at_iso
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function clearQualifyData(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // silent
  }
}

/**
 * Build a human-readable append block from qualification data.
 */
export function buildMessageBlock(data: QualifyData): string {
  const lines = ['---'];

  if (data.selected_place) {
    lines.push(`Plats: ${data.selected_place.name}`);
    if (data.selected_place.municipality) {
      lines.push(`Kommun: ${data.selected_place.municipality}`);
    }
    if (data.selected_place.landskap) {
      lines.push(`Landskap: ${data.selected_place.landskap}`);
    }
  }

  if (data.producer_name) {
    lines.push(`Verksamhet: ${data.producer_name}`);
  }

  if (data.offerings.length > 0) {
    lines.push(`Erbjudanden: ${data.offerings.join(', ')}`);
  }

  lines.push('---');
  return lines.join('\n');
}
